import { useEffect } from 'react';
import useDeletePaymentMethodMutation from '@/api/mutations/useDeletePaymentMethodMutation';
import useDisablePaymentMethodMutation from '@/api/mutations/useDisablePaymentMethodMutation';
import useEnablePaymentMethodMutation from '@/api/mutations/useEnablePaymentMethodMutation';
import useSetDefaultPaymentMethodMutation from '@/api/mutations/useSetDefaultPaymentMethodMutation';
import useListPaymentMethodsQuery from '@/api/queries/useListPaymentMethodsQuery';
import Button from '@/components/Button';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PaymentMethodCard from '@/modules/billing/components/PaymentMethodCard';
import { BillingPaymentMethodAddPath } from '@/modules/billing/paths';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import { delay } from '@/utils/async';
import { CreditCardIcon, PlusIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';

type PaymentMethodListLocationState = {
  paymentMethodAdded?: boolean;
};

const POLL_DELAY_MS = 3000;
const POLL_COUNT = 3;

const PaymentMethodList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { paymentMethods, isLoading, error, refetch } = useListPaymentMethodsQuery();

  const state = location.state as PaymentMethodListLocationState | null;

  // When a payment method is added, its final state is applied asynchronously.
  // There can be a short delay before the backend reflects the updated status.
  // To keep the UI in sync without long-lived polling, we refetch a few times
  // after returning from the setup flow, then stop.
  useEffect(() => {
    if (!state?.paymentMethodAdded) return;
    const run = async () => {
      for (let i = 0; i < POLL_COUNT; i++) {
        await refetch();
        if (i < POLL_COUNT - 1) await delay(POLL_DELAY_MS);
      }
    };
    void run();
    // Clear navigation state so this doesn't re-trigger on refresh or back/forward
    navigate(location.pathname, { replace: true });
  }, [state, refetch, navigate, location.pathname]);

  const enablePaymentMethod = useEnablePaymentMethodMutation();
  const disablePaymentMethod = useDisablePaymentMethodMutation();
  const deletePaymentMethod = useDeletePaymentMethodMutation();
  const setDefaultPaymentMethod = useSetDefaultPaymentMethodMutation();

  if (isLoading) {
    return (
      <>
        <SettingsHeader title="Payment Methods" icon={<CreditCardIcon className="w-6 h-6" />} />
        <div className="flex justify-center py-20">
          <SpinnerIcon />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SettingsHeader title="Payment Methods" icon={<CreditCardIcon className="w-6 h-6" />} />
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          Failed to load payment methods.
        </div>
      </>
    );
  }

  const methods = paymentMethods?.items || [];

  const sorted = [...methods].sort((a, b) => {
    const aDefault = Boolean(a?.card?.isDefault);
    const bDefault = Boolean(b?.card?.isDefault);
    if (aDefault !== bDefault) return aDefault ? -1 : 1;

    const aDisabled = Boolean(!a?.card?.isActive);
    const bDisabled = Boolean(!b.card?.isActive);
    if (aDisabled !== bDisabled) return aDisabled ? 1 : -1;

    return 0;
  });

  const handleEnablePaymentMethod = (id: string) => {
    enablePaymentMethod.mutate(id);
  };

  const handleDisablePaymentMethod = (id: string) => {
    disablePaymentMethod.mutate(id);
  };

  const handleDeletePaymentMethod = (id: string) => {
    deletePaymentMethod.mutate(id, {
      onSuccess: () => {
        toast.custom((t) => (
          <CustomToast
            id={t.id}
            message="Payment method deleted successfully!"
            visible={t.visible}
          />
        ));
      },
      onError: () => {
        toast.custom((t) => (
          <CustomToast
            id={t.id}
            message="Failed to delete payment method. Please try again."
            variant="error"
            visible={t.visible}
          />
        ));
      },
    });
  };

  const handleSetDefaultPaymentMethod = (id: string) => {
    setDefaultPaymentMethod.mutate(id, {
      onSuccess: () => {
        toast.custom((t) => (
          <CustomToast
            id={t.id}
            message="Default payment method updated successfully!"
            visible={t.visible}
          />
        ));
      },
      onError: () => {
        toast.custom((t) => (
          <CustomToast
            id={t.id}
            message="Failed to set default payment method. Please try again."
            variant="error"
            visible={t.visible}
          />
        ));
      },
    });
  };

  return (
    <>
      <SettingsHeader title="Payment Methods" icon={<CreditCardIcon className="w-6 h-6" />}>
        <Button
          onClick={() => navigate(BillingPaymentMethodAddPath)}
          icon={<PlusIcon className="w-5 h-5 mr-1" />}
          data-testid="payment-method-add-button"
        >
          Add payment method
        </Button>
      </SettingsHeader>

      <p className="mb-4 text-sm text-gray-600">
        Manage your saved payment methods for subscription billing.
      </p>

      {sorted.length === 0 ? (
        <div className="rounded-lg border p-6" data-testid="payment-method-empty-state">
          <p className="text-sm text-gray-700">You don’t have any saved payment methods yet.</p>

          <button
            type="button"
            onClick={() => navigate(BillingPaymentMethodAddPath)}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-black px-3 py-2 text-sm font-medium text-white"
            data-testid="payment-method-add-first-button"
          >
            <PlusIcon className="h-4 w-4" />
            Add your first payment method
          </button>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="payment-method-list">
          {sorted.map((method) => (
            <PaymentMethodCard
              key={method.id ?? method.paymentMethodId}
              method={method}
              onEnable={handleEnablePaymentMethod}
              onDisable={handleDisablePaymentMethod}
              onDelete={handleDeletePaymentMethod}
              onSetDefault={handleSetDefaultPaymentMethod}
              isLoadingEnable={
                enablePaymentMethod.isPending && enablePaymentMethod.variables === method.id
              }
              isLoadingDisable={
                disablePaymentMethod.isPending && disablePaymentMethod.variables === method.id
              }
              isLoadingDelete={
                deletePaymentMethod.isPending && deletePaymentMethod.variables === method.id
              }
              iseLoadingSetDefault={
                setDefaultPaymentMethod.isPending && setDefaultPaymentMethod.variables === method.id
              }
            />
          ))}
        </div>
      )}
    </>
  );
};

export default PaymentMethodList;
